import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaCheckCircle, FaTimesCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';
import { RootState } from '../store';
import { getAccountAwarePlanCta } from '../utils/accountAwareCta';
import type { AccountAwarePlanKind } from '../utils/accountAwareCta';

type Audience = 'workers' | 'employers';

const WORKER_PLANS = [
  {
    id: 'free',
    label: 'Free',
    price: 0,
    tagline: 'Get started — browse and apply manually',
    features: [
      { name: 'Browse all gigs', included: true },
      { name: 'Create profile', included: true },
      { name: 'Job matching', included: true },
      { name: 'Manual apply', included: true },
      { name: 'In-app match updates', included: true },
      { name: 'Auto-apply', included: false },
    ],
    cta: 'Sign Up Free',
    ctaLink: '/register?role=worker',
    ctaKind: 'worker-free' as AccountAwarePlanKind,
    border: 'border-gray-200',
    btn: 'bg-gray-800 text-white hover:bg-gray-900',
  },
  {
    id: 'basic',
    label: 'Automation Early Access',
    price: 9,
    tagline: 'Optional automation while the feature matures',
    features: [
      { name: 'Everything in Free', included: true },
      { name: 'Up to 20 supported automation attempts / week', included: true },
      { name: 'Review-first and pause controls', included: true },
      { name: 'Automatic submission only on supported destinations', included: true },
    ],
    cta: 'Start Early Access — $9/mo',
    ctaLink: '/register?role=worker&redirect=%2Fsubscription',
    ctaKind: 'worker-automation' as AccountAwarePlanKind,
    border: 'border-2 border-blue-600',
    btn: 'bg-blue-600 text-white hover:bg-blue-700',
  },
];

const EMPLOYER_PLANS = [
  {
    id: 'starter',
    label: 'Starter',
    price: 99,
    tagline: '5 gig posts per month',
    features: [
      { name: '5 job postings / month', included: true },
      { name: 'Profile & preference matching', included: true },
      { name: 'Employer dashboard', included: true },
      { name: 'Worker search & filtering', included: true },
      { name: 'Analytics dashboard', included: true },
      { name: 'Candidate status & source visibility', included: true },
    ],
    cta: 'View Employer Plans',
    ctaLink: '/register?role=employer&redirect=%2Femployer%2Fsubscription',
    ctaKind: 'employer-plan' as AccountAwarePlanKind,
    border: 'border-gray-200',
    btn: 'bg-gray-800 text-white hover:bg-gray-900',
  },
  {
    id: 'growth',
    label: 'Growth',
    price: 249,
    tagline: '20 gig posts per month',
    features: [
      { name: '20 job postings / month', included: true },
      { name: 'Profile & preference matching', included: true },
      { name: 'Employer dashboard', included: true },
      { name: 'Worker search & filtering', included: true },
      { name: 'Analytics dashboard', included: true },
      { name: 'Candidate status & source visibility', included: true },
    ],
    cta: 'View Employer Plans',
    ctaLink: '/register?role=employer&redirect=%2Femployer%2Fsubscription',
    ctaKind: 'employer-plan' as AccountAwarePlanKind,
    border: 'border-2 border-blue-600',
    btn: 'bg-blue-600 text-white hover:bg-blue-700',
  },
  {
    id: 'enterprise',
    label: 'Enterprise',
    price: 499,
    tagline: 'Unlimited gig posts',
    features: [
      { name: 'Unlimited job postings', included: true },
      { name: 'Profile & preference matching', included: true },
      { name: 'Employer dashboard', included: true },
      { name: 'Worker search & filtering', included: true },
      { name: 'Analytics dashboard', included: true },
      { name: 'Candidate status & source visibility', included: true },
    ],
    cta: 'View Employer Plans',
    ctaLink: '/register?role=employer&redirect=%2Femployer%2Fsubscription',
    ctaKind: 'employer-plan' as AccountAwarePlanKind,
    border: 'border-purple-200',
    btn: 'bg-purple-600 text-white hover:bg-purple-700',
  },
];

const WORKER_FAQ = [
  {
    q: 'What does Automation Early Access include?',
    a: 'The $9 monthly worker plan includes up to 20 supported automation attempts per week, with review-first and pause controls. Manual applications, matching, and in-app updates remain free.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Workers can browse, match, and apply manually for free, forever. The optional $9 monthly early-access plan adds supported application automation. No credit card is required for a free account.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit and debit cards through Stripe. All payments are securely processed.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'You can request cancellation from your subscription page. When Stripe confirms the request, StageCrew schedules cancellation for the end of the current billing period and shows that status in your account.',
  },
  {
    q: 'What does auto-apply do?',
    a: 'Auto-apply uses your saved role, location, rate, and availability to find relevant gigs and attempt submission where the posting destination supports it. You can pause it or use review-first mode.',
  },
];

const EMPLOYER_FAQ = [
  {
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit and debit cards through Stripe. All payments are securely processed.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'You can request cancellation from your subscription page. When Stripe confirms the request, StageCrew schedules cancellation for the end of the current billing period and shows that status in your account.',
  },
  {
    q: 'Do employers pay per hire?',
    a: 'Employer plans are flat monthly subscriptions. Post gigs, match with workers, and hire — no per-hire fees on subscription plans.',
  },
];

const PricingPage = () => {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const [audience, setAudience] = useState<Audience>(
    isAuthenticated && user?.role === 'employer' ? 'employers' : 'workers',
  );
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'employer') {
      setAudience('employers');
    } else if (isAuthenticated && user?.role === 'worker') {
      setAudience('workers');
    }
  }, [isAuthenticated, user?.role]);

  const plans = audience === 'workers' ? WORKER_PLANS : EMPLOYER_PLANS;
  const faqItems = audience === 'workers' ? WORKER_FAQ : EMPLOYER_FAQ;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Pricing — StageCrew | AV & Stagehand Gig Plans"
        description="Simple pricing for AV workers and employers. Workers can use StageCrew free or join Automation Early Access for $9/month. Employer plans start at $99/month."
        url="https://stagecrew.app/pricing"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'StageCrew Plans',
          description: 'AV & stagehand gig marketplace — plans for workers and employers.',
          brand: { '@type': 'Brand', name: 'StageCrew' },
          offers: [
            { '@type': 'Offer', name: 'Worker Free', price: '0', priceCurrency: 'USD' },
            { '@type': 'Offer', name: 'Worker Automation Early Access', price: '9', priceCurrency: 'USD' },
            { '@type': 'Offer', name: 'Employer Starter', price: '99', priceCurrency: 'USD' },
            { '@type': 'Offer', name: 'Employer Growth', price: '249', priceCurrency: 'USD' },
            { '@type': 'Offer', name: 'Employer Enterprise', price: '499', priceCurrency: 'USD' },
          ],
        }}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Simple, Transparent Pricing</h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            No hidden fees. No per-hire charges. Pick a plan that fits your needs and cancel anytime.
          </p>
        </div>

        {/* Audience Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-gray-200 rounded-full p-1 flex" role="group" aria-label="Pricing audience">
            <button
              type="button"
              onClick={() => setAudience('workers')}
              aria-pressed={audience === 'workers'}
              className={`px-6 py-2 rounded-full font-medium text-sm transition ${
                audience === 'workers'
                  ? 'bg-white text-gray-900 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              For Workers
            </button>
            <button
              type="button"
              onClick={() => setAudience('employers')}
              aria-pressed={audience === 'employers'}
              className={`px-6 py-2 rounded-full font-medium text-sm transition ${
                audience === 'employers'
                  ? 'bg-white text-gray-900 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              For Employers
            </button>
          </div>
        </div>

        {audience === 'workers' && (
          <p className="text-center text-sm text-gray-500 -mt-6 mb-12">
            Automation attempts are available only where the posting destination supports submission.
          </p>
        )}

        {/* Plan Cards */}
        <div className={`grid grid-cols-1 ${audience === 'workers' ? 'md:grid-cols-2 max-w-4xl' : 'md:grid-cols-3 max-w-5xl'} gap-8 mx-auto mb-16`}>
          {plans.map((plan) => {
            const cta = getAccountAwarePlanCta({
              isAuthenticated,
              role: user?.role,
              kind: plan.ctaKind,
              signedOutLabel: plan.cta,
              signedOutPath: plan.ctaLink,
            });

            return (
              <div
                key={plan.id}
                className={`bg-white rounded-xl shadow-lg p-8 ${plan.border} relative flex flex-col`}
              >
              <h2 className="text-2xl font-bold mb-2">{plan.label}</h2>
              <div className="flex items-baseline mb-1">
                <span className="text-5xl font-bold">
                  {plan.price === 0 ? 'Free' : `$${plan.price}`}
                </span>
                {plan.price > 0 && <span className="text-gray-500 ml-2">/month</span>}
              </div>
              <p className="text-sm text-gray-500 mb-6">{plan.tagline}</p>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((f) => (
                  <li key={f.name} className="flex items-center">
                    {f.included ? (
                      <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                    ) : (
                      <FaTimesCircle className="text-gray-300 mr-3 flex-shrink-0" />
                    )}
                    <span className={f.included ? '' : 'text-gray-400'}>{f.name}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={cta.path}
                className={`w-full text-center px-6 py-3 rounded-lg font-bold transition block ${plan.btn}`}
              >
                {cta.label}
              </Link>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqItems.map((item, i) => (
              <div key={i} className="bg-white rounded-lg shadow">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`pricing-faq-answer-${i}`}
                  className="w-full flex items-center justify-between px-6 py-4 text-left font-medium text-gray-900 hover:bg-gray-50 transition rounded-lg"
                >
                  <span>{item.q}</span>
                  {openFaq === i ? (
                    <FaChevronUp aria-hidden="true" className="text-gray-400 flex-shrink-0 ml-4" />
                  ) : (
                    <FaChevronDown aria-hidden="true" className="text-gray-400 flex-shrink-0 ml-4" />
                  )}
                </button>
                {openFaq === i && (
                  <div id={`pricing-faq-answer-${i}`} className="px-6 pb-4 text-gray-600">{item.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-500 mb-4">
            Questions? Reach out at{' '}
            <a href="mailto:edwin@stagecrew.app" className="text-blue-600 hover:underline">
              edwin@stagecrew.app
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
